import {
  addToast,
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import * as Sentry from "@sentry/react";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeCheckbox } from "@/Components/NextBase/LimeCheckbox";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import { getExitChargeSaleUrl } from "@/pages/Dashboard/CustomerSubscriptionOverview/components/subscriptionInvoiceUtils";
import { earlyExitChargeReason } from "@/pages/Dashboard/Subscriptions/commitmentPlanTerm";
import { getApiErrorCode, getApiErrorMessage } from "@/utils/api-error";
import { formatCurrency } from "../../../../../../../../shared/utils/utils";

type CancelFormValues = {
  removeCustomerFromAppointments: boolean;
  isExitChargeWaived: boolean;
  /** What to raise for the charge once the cancellation has gone through. */
  issueExitCharge: "none" | "estimate" | "invoice";
};

export function CustomerSubscriptionCancelModal({
  isOpen,
  onClose,
  customerSubscriptionId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number;
}) {
  const form = useForm<CancelFormValues>({
    initialValues: {
      removeCustomerFromAppointments: false,
      isExitChargeWaived: false,
      issueExitCharge: "none",
    },
  });

  // Fetched only once the modal opens: a card list would otherwise quote every member.
  const {
    data: earlyExitData,
    error: earlyExitError,
    refetch: refetchEarlyExit,
  } = api.customer.useGetCustomerSubscriptionEarlyExitEstimate({
    customerSubscriptionId,
    enabled: isOpen,
  });
  // Not `isLoading`: that is already false in the render where the query turns on.
  const isEarlyExitKnown = earlyExitData !== undefined;
  const earlyExit = earlyExitData?.estimate ?? null;

  const { data: currency } = api.values.useGetCurrency({});
  const exitChargeLabel = formatCurrency({
    amount: (earlyExit?.chargeCents ?? 0) / 100,
    currency: currency ?? "",
    locale: i18n.locale,
  });

  const { mutateAsync: updateCustomerSubscription, isPending } =
    api.customer.useUpdateCustomerSubscription();

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const { mutateAsync: issueExitCharge } =
    api.customer.useIssueCommitmentExitChargeDocument();

  const { isFeatureEnabled } = useClientFeature();
  const canIssueDocuments =
    isFeatureEnabled("taxRegister") &&
    isFeatureEnabled("subscriptionsTaxRegisterIntegration");

  /** Best effort: the charge stays recorded and issuable from the Vezave view either way. */
  async function issueRecordedCharge(commitmentId: number) {
    const documentType = form.values.issueExitCharge;
    if (documentType === "none") {
      return;
    }
    // An invoice needs a payment, which only the till takes; it prints there too.
    if (documentType === "invoice") {
      window.location.href = getExitChargeSaleUrl(
        { customerSubscriptionId, commitmentId },
        window.location.href,
      );
      return;
    }
    try {
      await issueExitCharge({
        customerSubscriptionId,
        commitmentId,
        body: { sendOnEmail: false },
      });
      addToast({
        title: t`Predračun za odstopnino je izdan`,
        color: "success",
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  const handleCancel = async () => {
    try {
      await updateCustomerSubscription({
        customerSubscriptionId,
        body: {
          status: "canceled",
          removeCustomerFromAppointments:
            form.values.removeCustomerFromAppointments,
          ...(earlyExit && {
            commitmentExit: {
              acknowledgedChargeCents: earlyExit.chargeCents,
              waived: form.values.isExitChargeWaived,
            },
          }),
        },
      });
      addToast({
        title: t`Paket preklican`,
        description: t`Paket je bil uspešno preklican`,
        color: "success",
      });
      // Outside the cancel transaction, which must not wait on the tax register; a failed
      // issue leaves the recorded charge for the Vezave view to retry.
      if (earlyExit && !form.values.isExitChargeWaived) {
        await issueRecordedCharge(earlyExit.id);
      }
      handleClose();
    } catch (error) {
      // The charge is re-derived inside the transaction, so a stale quote means the
      // modal is showing an amount that has since moved: fetch the new one rather than
      // letting the retry fail identically.
      if (getApiErrorCode(error) === "COMMITMENT_EXIT_CHARGE_STALE") {
        void refetchEarlyExit();
      }
      addToast({
        title: t`Napaka`,
        description:
          getApiErrorMessage(error) ?? t`Paketa ni bilo mogoče preklicati.`,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={handleClose}
      data-identifier="cancel-subscription-modal"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Preklic paketa</Trans>
        </ModalHeader>
        <ModalBody>
          {earlyExitError && !isEarlyExitKnown && (
            <LimeAlert
              color="danger"
              data-identifier="commitment-exit-charge-error"
              title={t`Vezave ni bilo mogoče preveriti`}
              description={t`Poskusite znova — paketa ne prekličemo, dokler ne vemo, ali je vezan.`}
            />
          )}
          {!isEarlyExitKnown && !earlyExitError && (
            <Skeleton className="h-20 w-full rounded-lg" />
          )}
          {earlyExit && (
            <>
              <LimeAlert
                color="warning"
                data-identifier="commitment-exit-charge-warning"
                title={t`Paket je še vezan`}
                description={
                  <div className="flex flex-col gap-1">
                    <p>
                      <Trans>
                        Strošek predčasne prekinitve:{" "}
                        <strong>{exitChargeLabel}</strong>
                      </Trans>
                    </p>
                    <p className="text-small">
                      {earlyExitChargeReason(earlyExit)}
                    </p>
                  </div>
                }
              />
              <LimeCheckbox
                size="sm"
                data-identifier="commitment-exit-charge-waive"
                {...form.getInputProps("isExitChargeWaived", {
                  type: "checkbox",
                })}
              >
                <Trans>Stroška ne zaračunamo.</Trans>
              </LimeCheckbox>
              {canIssueDocuments && !form.values.isExitChargeWaived && (
                <LimeSelect
                  size="sm"
                  label={t`Dokument za odstopnino`}
                  data-identifier="commitment-exit-charge-document"
                  items={[
                    { key: "none", label: t`Izdam pozneje` },
                    { key: "estimate", label: t`Izdaj predračun` },
                    { key: "invoice", label: t`Izdaj račun` },
                  ]}
                  selectedKeys={[form.values.issueExitCharge]}
                  onSingleItemChange={(value) =>
                    form.setFieldValue(
                      "issueExitCharge",
                      value as CancelFormValues["issueExitCharge"],
                    )
                  }
                />
              )}
            </>
          )}
          <LimeSwitch
            size="sm"
            {...form.getInputProps("removeCustomerFromAppointments", {
              type: "checkbox",
            })}
          >
            <Trans>
              Odstrani stranko iz vseh prihodnjih terminov, kjer je bil
              uporabljen ta paket.
            </Trans>
          </LimeSwitch>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} isDisabled={isPending}>
            <Trans>Prekliči</Trans>
          </Button>
          <Button
            data-identifier="cancel-subscription-confirm-btn"
            color="danger"
            variant="solid"
            onPress={handleCancel}
            // Never cancel while it is unknown whether the package is committed.
            isDisabled={!isEarlyExitKnown}
            isLoading={isPending}
          >
            <Trans>Potrdi preklic</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
}
